import {
    Manager as CMKManager,
    CMKErrorCode,
    KDFUnionAlgo,
} from '@reolink-web/cloud-rs-crypto-bindgen';
import { getCMKById, getExtractCMK, getCMKS } from '@/api/cmk';
import 'crypto-js/core';
import MD5 from 'crypto-js/md5';
import Sha256 from 'crypto-js/sha256';
import Base64 from 'crypto-js/enc-base64';
import { CMKInstance, IReoEncryptKey } from '@reolink-cloud/interface';
import { registerMessageHandler } from '@/utils/sw';
import { IGetKeyBase64Message, IMessagePayload } from '@/types/sw';

CMKManager.enableCrossPageCache();

// eslint-disable-next-line @typescript-eslint/no-floating-promises
CMKManager.init({
    descriptor: getCMKById,
    extract: getExtractCMK,
    cmks: async () => ( await getCMKS()).items
}, {
    wasm: false
});

let isInit: Promise<void> | boolean | undefined;

export const waitInit = async (): Promise<void> => {

    if ( typeof isInit === 'undefined' ) {

        isInit = CMKManager.init({}, {
            wasm: true
        });

    }

    if ( isInit !== true ) {

        await isInit;

        isInit = true;

    }

};

export const getCMKList = CMKManager.getCMKList;

const cmkErrorCallback: Array<( cmkId: string ) => any> = [];
export const getCmkInstance = async ( cmkId: string ): ReturnType<typeof CMKManager.getCMK> => {

    await waitInit();

    const instance =  await CMKManager.getCMK( cmkId );
    if ( instance.isErr()) {

        for ( const callback of cmkErrorCallback ) {

            callback( cmkId );

        }

    }

    return instance;

};

export const registerCmkErrorCallback = ( callback: ( cmkId: string ) => any ): void => {

    if ( typeof callback === 'function' ) {

        cmkErrorCallback.push( callback );

    }

};

export const getCmkHeaders = (
    encrypt: IReoEncryptKey,
    keySeed: string,
    instance: CMKInstance
): Record<string, string> => {

    const keyBase64 = CMKManager.getHash(
        encrypt.kdf as KDFUnionAlgo,
        encrypt.iterations,
        instance.gen_shared_key( encrypt.devicePublicKey ),
        keySeed
    );

    const keyMD5Base64 = Base64.stringify( MD5( Base64.parse( keyBase64 )));
    const keySha256Base64 =  Base64.stringify( Sha256( Base64.parse( keyBase64 )));
    if ( encrypt.mode.includes( 's3' )) {

        return {
            'x-amz-server-side-encryption-customer-key': keyBase64,
            'x-amz-server-side-encryption-customer-key-MD5': keyMD5Base64,
            'x-amz-server-side-encryption-customer-algorithm': encrypt.algo
        };

    } else if ( encrypt.mode === 'azure-sse-c' ) {

        return {
            'x-ms-encryption-algorithm':encrypt.algo,
            'x-ms-encryption-key':keyBase64,
            'x-ms-encryption-key-sha256': keySha256Base64
        };

    }

    return {};

};

registerMessageHandler( async ( message: IMessagePayload<IGetKeyBase64Message> ) => {

    if ( !message || ( message.type !== 'getKeyBase64' && message.type !== 'getSalt' )) {

        return;

    }

    await waitInit();
    const { kdf, iterations, devicePublicKey, keySeed, cmkId } = message.payload;
    const cmkInstance = await getCmkInstance( cmkId );
    if ( cmkInstance.isErr()) {

        throw cmkInstance.error;

    }
    console.log( 'Service Worker request',  message );
    if ( message.type === 'getSalt' ) {

        return cmkInstance.value.gen_shared_key( devicePublicKey );

    }
    const keyBase64 = CMKManager.getHash(
        kdf,
        iterations,
        cmkInstance.value.gen_shared_key( devicePublicKey ),
        keySeed
    );
    return keyBase64;

});

export { CMKManager, CMKErrorCode };
